import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState, useCallback } from "react";

type UseAutocompleteSuggestionsReturn = {
  suggestions: google.maps.places.AutocompleteSuggestion[];
  isLoading: boolean;
  resetSession: () => void;
  getPlaceDetails: (placeId: string) => Promise<any>;
};

export function useAutocompleteSuggestions(
  inputString: string,
  requestOptions: Partial<google.maps.places.AutocompleteRequest> = {}
): UseAutocompleteSuggestionsReturn {
  const placesLib = useMapsLibrary("places");

  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken>(null);
  const requestOptionsRef = useRef(requestOptions);
  const lastInputRef = useRef<string>("");

  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  // Update ref when requestOptions change
  useEffect(() => {
    requestOptionsRef.current = requestOptions;
  }, [requestOptions]);

  useEffect(() => {
    if (!placesLib) return;

    // Prevent duplicate requests for same input
    if (lastInputRef.current === inputString) return;
    lastInputRef.current = inputString;

    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    // Clear suggestions if input is empty or too short
    if (inputString === "" || inputString.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const request: google.maps.places.AutocompleteRequest = {
      ...requestOptionsRef.current,
      input: inputString,
      sessionToken: sessionTokenRef.current as any,
    };

    setIsLoading(true);
    AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then(
      (res: any) => {
        // Only update suggestions if this is still the current input
        if (lastInputRef.current === inputString) {
          setSuggestions(res.suggestions || []);
          setIsLoading(false);
        }
      },
      (error: any) => {
        console.error("Error fetching autocomplete suggestions:", error);
        if (lastInputRef.current === inputString) {
          setSuggestions([]);
          setIsLoading(false);
        }
      }
    );
  }, [placesLib, inputString]);

  // New function to fetch selected place details
  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<any> => {
      if (!placesLib || !placeId) return null;

      try {
        // Usar a API Places Details
        const placesService = new google.maps.places.PlacesService(
          document.createElement("div")
        );

        return new Promise((resolve, reject) => {
          placesService.getDetails(
            {
              placeId: placeId,
              fields: ["geometry", "formatted_address", "name"],
            },
            (place, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                place
              ) {
                resolve(place);
              } else {
                reject(new Error(`Error fetching place details: ${status}`));
              }
            }
          );
        });
      } catch (error) {
        console.error("Error fetching place details:", error);
        return null;
      }
    },
    [placesLib]
  );

  return {
    suggestions,
    isLoading,
    resetSession: () => {
      sessionTokenRef.current = null;
      setSuggestions([]);
    },
    getPlaceDetails,
  };
}
